import React, { useMemo } from 'react';
import TimeSeries, { TimeSeriesPoint } from '../../../../ui/TimeSeries';
import type {
  LiquidityModelEvolutionCell,
  LiquidityModelEvolutionPayload,
  LiquidityModelEvolutionRow,
} from '../../../../../api/rs-hg/liquidity/hooks/queries';

interface LiquidityModelEvolutionChartProps {
  payload: LiquidityModelEvolutionPayload;
  volumeOrDays: 'volume' | 'days';
  positionName?: string;
}

const LiquidityModelEvolutionChart: React.FC<
  LiquidityModelEvolutionChartProps
> = ({ payload, volumeOrDays, positionName }) => {
  const dates: string[] = payload?.dates || [];
  const liquidityTableData: LiquidityModelEvolutionRow[] =
    payload?.liquidity_table || [];

  const timeSeries: TimeSeriesPoint[] = useMemo(() => {
    if (!dates.length || !liquidityTableData.length) return [];

    const rowsToProcess: LiquidityModelEvolutionRow[] = positionName
      ? liquidityTableData.filter(
          (r: LiquidityModelEvolutionRow) => r?.position_name === positionName,
        )
      : liquidityTableData;

    return dates.map((date) => {
      let total: number | null = null;
      for (const row of rowsToProcess) {
        const dateData = row?.[date] as LiquidityModelEvolutionCell | undefined;
        if (dateData && typeof dateData === 'object') {
          if (
            volumeOrDays === 'volume' &&
            typeof dateData.used_volume === 'number'
          ) {
            total = (total ?? 0) + dateData.used_volume;
          }
          if (
            volumeOrDays === 'days' &&
            typeof dateData.used_days === 'number'
          ) {
            total = (total ?? 0) + dateData.used_days;
          }
        }
      }
      return { date, value: total };
    });
  }, [dates, liquidityTableData, volumeOrDays, positionName]);

  if (!timeSeries.length) {
    return null;
  }

  return (
    <TimeSeries
      title={positionName}
      data={timeSeries}
      fileName={`${positionName || 'liquidity-position'}-${timeSeries[0]?.date}-liquidity-time-series-${timeSeries[timeSeries.length - 1]?.date}`}
      heightClassName="h-64"
      nullLine={false}
    />
  );
};

export default LiquidityModelEvolutionChart;
